import { useEffect, useRef, useState } from 'react';
import { DRAG_THRESHOLD_PX } from '../components/calendar/weekGrid';
import { dayDrop } from '../domain/calendarDrop';

// Dragging an event to another day on the month grid (#571).
//
// The month's half of the move useLessonDrag.js does on the time grid, and
// the same mechanics on purpose: pointer events rather than the HTML5 drag API,
// so a finger moves a chip as a mouse does; nothing moves until the pointer has
// travelled past `DRAG_THRESHOLD_PX`, so a press and release in place is still
// the click that opens the popover; and the click a drag's own release produces
// is swallowed for a moment afterwards.
//
// The cell under the pointer is found by its `data-date`, through
// `elementFromPoint`, rather than by listening on every cell: the chip keeps
// the pointer while it is pressed (a touch is captured implicitly), so the
// cells would never hear it.
//
// What the drop means is the domain's (`dayDrop` in domain/calendarDrop.js) and
// what it writes is the page's; this only says which day the event was let go
// on.

// How long after a drag a click is taken as that drag's own release — the
// same allowance, and the same reasoning, as useLessonDrag.js.
const CLICK_AFTER_DRAG_MS = 400;

const cellDateAt = (x, y) => {
  if (typeof document === 'undefined' || !document.elementFromPoint) return null;
  return document.elementFromPoint(x, y)?.closest?.('[data-date]')?.getAttribute('data-date') || null;
};

export default function useMonthDrag({ onMoveEvent }) {
  const [drag, setDrag] = useState(null);
  // The gesture as of the last event, for the release to read without calling
  // back into the page from inside a state updater (see useLessonDrag.js).
  const dragRef = useRef(null);
  const onMoveRef = useRef(onMoveEvent);
  onMoveRef.current = onMoveEvent;
  const clickSuppressedUntil = useRef(0);
  const update = (value) => {
    dragRef.current = value;
    setDrag(value);
  };

  const startMove = (occurrence, e) => {
    if (!onMoveEvent) return;
    if (e.button !== undefined && e.button !== 0) return;
    update({
      occurrence,
      anchorClientX: e.clientX,
      anchorClientY: e.clientY,
      committed: false,
      overDate: null,
    });
  };

  const active = Boolean(drag);
  useEffect(() => {
    if (!active) return undefined;

    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const travelled = Math.hypot(ev.clientX - d.anchorClientX, ev.clientY - d.anchorClientY);
      const committed = d.committed || travelled > DRAG_THRESHOLD_PX;
      update({ ...d, committed, overDate: committed ? cellDateAt(ev.clientX, ev.clientY) : null });
    };

    const commit = () => {
      const d = dragRef.current;
      update(null);
      if (!d || !d.committed) return;
      clickSuppressedUntil.current = Date.now() + CLICK_AFTER_DRAG_MS;
      const to = dayDrop(d.occurrence.date, d.overDate);
      if (to) onMoveRef.current?.(d.occurrence, to);
    };

    const cancel = () => update(null);
    const onKeyDown = (ev) => { if (ev.key === 'Escape') cancel(); };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', commit);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', commit);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [active]);

  // Whether a click arriving now is a drag's own release rather than a click.
  const clickSuppressed = () => Date.now() < clickSuppressedUntil.current;

  // The event being carried, once it is a drag, and the day it would land on.
  const movingId = drag?.committed ? drag.occurrence.id : null;
  const overDate = drag?.committed ? drag.overDate : null;

  return { startMove, clickSuppressed, movingId, overDate };
}
